import { useState } from "react";
import { useModal } from "../contexts";
import { ChatGPTField, Workflow } from "../interfaces";
import Modal from "./Modal";
import TextInputField from "./TextInputField";
import { deleteData, postData, putData } from "../backend";
import WorkflowSourceFieldTable from "./WorkflowSourceFieldTable";
import { CheckIcon } from "@heroicons/react/outline";
import PortantAILabel from "./PortantAILabel";
import useUser from "../hooks/useUser";
import PayWallModalPortantAI from "./PayWallModalPortantAI";
import { KeyedMutator } from "swr";
import { useTranslation } from "react-i18next";

interface Props {
  workflow: Workflow
  mutateWorkflow: KeyedMutator<Workflow>;
  field?: ChatGPTField
}

function WorkflowChatGPTModal(props: Props) {
  const { workflow, mutateWorkflow, field } = props;
  const { openModal, closeModal } = useModal();

  const [fieldData, setFieldData] = useState<ChatGPTField>({
    id: field?.id ?? '',
    index: field?.index ?? Number.MAX_VALUE,
    fieldType: field?.fieldType ?? "TEXT",
    fieldName: field?.fieldName ?? '',
    prompt: field?.prompt ?? '',
  })
  const [hasPromptValue, setHasPromptValue] = useState(false);

  const { t } = useTranslation();

  const [isSaving, setIsSaving] = useState(false);

  const { user } = useUser();

  async function handleSave() {
    setIsSaving(true);

    if (field)
      await putData(`/chatgpt/fields/${field!.id}/`, { fieldType: fieldData.fieldType, fieldName: fieldData.fieldName, prompt: fieldData.prompt })
        .catch(console.error)
        .finally(() => setIsSaving(false));
    else
      await postData(`/workflows/sources/${workflow.source!.id}/chatgpt-fields/`, { fieldName: fieldData.fieldName, prompt: fieldData.prompt })
        .catch(console.error)
        .finally(() => setIsSaving(false));

    mutateWorkflow();
    closeModal();
  }

  async function handleDelete() {
    await deleteData(`/chatgpt/fields/${field!.id}/`)
      .catch(console.error)

    mutateWorkflow();
    closeModal();
  }

  function handleChange(e: React.ChangeEvent<HTMLTextAreaElement>) {
    setFieldData(prev => ({ ...prev, prompt: e.target.value }));
    setHasPromptValue(!!e.target.value);
  }

  return (
    <Modal
      title={
        <div className="flex items-center gap-4">
          <h3 className="font-gilroy font-semibold text-xl text-gray-900 flex-shrink-0">{t("AI Content Field")}</h3>
          <PortantAILabel />
        </div>
      }
      subtitle={t("Use the power of ChatGPT to generate content based on a prompt configured with your source fields")}
      primaryButtonProps={{
        onClick: handleSave,
        text: isSaving ? t("Saving...") : (!!field ? t("Save") : t("Create")),
        disabled: isSaving || !fieldData.fieldName || !hasPromptValue
      }}
      secondaryButtonProps={{
        onClick: handleDelete,
        text: t("Delete"),
        className: `btn-red ${!!field ? 'block' : 'hidden'}`
      }}
      size="xl"
      className="gap-2 flex flex-col"
    >
      <div className="flex items-center gap-2 w-full ">
        <TextInputField
          label={t("Field Name")}
          className="w-7/12 flex-grow"
          value={fieldData.fieldName || ""}
          onChange={(value) => setFieldData(prev => ({ ...prev, fieldName: value }))}
        />

        {/* NOTE: Image field types are currently disabled */}
        {/* <SelectField label="Field Type" className="w-1/4" value={fieldType || "TEXT"} onChange={setFieldType}>
            <option value="TEXT">Text</option>
            <option value="IMAGE">Image</option>
          </SelectField> */}

        {!user?.subscription?.featurePortantAi &&
          <div className="flex flex-col font-gilroy font-semibold ml-auto">
            <div className="text-sm text-gray-600">
              {t("Usage")}
            </div>
            <div className="flex items-center gap-2">
              <div>
                {user?.aiUsage}/<span className="text-xs">10</span>
              </div>
              <button className="text-sm text-blue cursor-pointer hover:underline" onClick={() => openModal(<PayWallModalPortantAI />)}>
                {t("Upgrade")}
              </button>
            </div>
          </div>
        }

      </div>

      <div className="flex gap-2 mt-2 w-full">

        <div className="relative border-2 rounded border-gray-400 w-7/12 flex-shrink-0 flex flex-col justify-between">
          <div className="absolute text-xs left-2 -top-2 bg-white z-10 px-1 font-gilroy font-semibold text-gray-600">
            {t("Prompt")}
          </div>
          <textarea value={fieldData?.prompt} onChange={handleChange} className="w-full h-full outline-none min-h-[12rem] p-2" />
          <div className="bg-gray-100 rounded-b-sm p-2 flex flex-col gap-1 select-none text-xs">
            <div className="font-semibold font-gilroy text-gray-600">
              {t("Examples")}
            </div>
            <ul className="list-disc ml-3">
              <li>{t("Write a [short/long] email to invite [name] to a demo of [product/service]")}</li>
              <li>{t("Write the key responsibilities and qualifications for a [position] role")}</li>
              <li>{t("Write a [short/long] social media marketing plan for [product/brand]")}</li>
            </ul>
          </div>
        </div>

        <div className="w-5/12">
          <WorkflowSourceFieldTable workflow={workflow} mutateWorkflow={mutateWorkflow} hideChatGPTField indexChatGPTField={field?.index} />
        </div>

      </div>

      <div className="flex justify-between items-center w-full">
        <div className="flex flex-col">
          <div className="text-xs text-gray-600">
            <div>
              {t("ChatGPT is trained on data collected from the internet and may produce")} <b>{t("inaccurate")}</b> {("or")} <b>{t("incorrect")}</b>{t("content.")}
            </div>
            <div className="flex items-center gap-1">
              {t("Please consider using a")} <a className="text-blue underline cursor-pointer" href=" https://www.youtube.com/watch?v=q-aATNuVIUU" target="_blank" rel="noreferrer">{t("Review Block")}</a> {!!workflow.review && <CheckIcon className="w-4 h-4 text-green" />} {t("when generating content using ChatGPT.")} <a className="text-blue underline" href="https://www.portant.co/terms-and-conditions" target="_blank" rel="noreferrer">{t("Terms and Conditions")}</a>
            </div>
          </div>
        </div>
      </div>
    </Modal>
  );
}

export default WorkflowChatGPTModal;
